/*
 * <variant-media-scroll>
 * ----------------------
 * Mobile-only (<=767px): after a variant change updates the product media, smoothly scroll the
 * viewport up to the media gallery so the newly selected variant's image is in view. Layers on top
 * of existing variant/gallery logic — it never changes selection or media behaviour itself.
 *
 * Hook: the product form's hidden <input name="id"> change — the theme's canonical variant-change
 *   signal (the gallery swaps the hero synchronously on this same event). A requestAnimationFrame
 *   guarantees we run AFTER that synchronous swap regardless of script order, then we wait for the
 *   new image to load/decode so we don't land on a blank/lazy slot.
 *
 * Accepts (data attributes):
 *   - data-target: CSS selector of the media gallery container to scroll to (required)
 *   - data-form:   product form id prefix, used to ignore other forms (quick-view, etc.) (optional)
 */
if (!customElements.get('variant-media-scroll')) {
  class VariantMediaScroll extends HTMLElement {
    connectedCallback() {
      this.target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
      this.formId = this.dataset.form || '';
      if (!this.target) return; // nothing to scroll to — fail silently

      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onChange = this.onChange.bind(this);
      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._timer) clearTimeout(this._timer);
      this._cleanupImg();
    }

    onChange(event) {
      const input = event.target;
      if (!input || !input.matches || !input.matches('input[name="id"]')) return;

      // Scope to THIS product's form so quick-view / other forms never trigger a scroll here.
      // Note: use getAttribute('id') — `form.id` returns the <input name="id"> control (named
      // form-control access shadows the form's id property), not the form's id string.
      if (this.formId) {
        const form = input.form;
        const formId = form && form.getAttribute('id');
        if (!formId || formId.indexOf(this.formId) !== 0) return;
      }

      if (!this.isMobile()) return; // desktop is left completely unchanged

      this.scheduleScroll();
    }

    isMobile() {
      const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
      return this.mobileQuery.matches && (coarsePointer || 'ontouchstart' in window);
    }

    scheduleScroll() {
      // Debounce: cancel any pending scroll so only the final selection scrolls once.
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._timer) clearTimeout(this._timer);
      this._cleanupImg();

      // rAF runs after every synchronous change listener (including the gallery's hero swap).
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.waitForMediaThenScroll();
      });
    }

    waitForMediaThenScroll() {
      const img = this.target.querySelector('img');

      // No image, or it's already loaded → scroll right away.
      if (!img || img.complete) {
        this.scrollToTarget();
        return;
      }

      // Otherwise wait for the new/lazy image to load, with a safety cap so we never hang.
      this._img = img;
      this._onImgLoad = () => {
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
        this._cleanupImg();
        this.scrollToTarget();
      };
      img.addEventListener('load', this._onImgLoad, { once: true });

      this._timer = setTimeout(() => {
        this._timer = null;
        this._cleanupImg();
        this.scrollToTarget();
      }, 600);
    }

    _cleanupImg() {
      if (this._img && this._onImgLoad) {
        this._img.removeEventListener('load', this._onImgLoad);
      }
      this._img = null;
      this._onImgLoad = null;
    }

    headerOffset() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      const parsed = parseInt(raw, 10);
      return isNaN(parsed) ? 0 : parsed;
    }

    scrollToTarget() {
      if (!this.target || !this.target.isConnected) return;

      const top = this.target.getBoundingClientRect().top + window.scrollY - this.headerOffset() - 12;
      const behavior = this.reducedMotionQuery.matches ? 'auto' : 'smooth';

      window.scrollTo({ top: Math.max(top, 0), behavior: behavior });
    }
  }

  customElements.define('variant-media-scroll', VariantMediaScroll);
}
